/**
 * The shell's own reads.
 *
 * Three, and each is the shell's rather than a page's: the genre tree hangs off
 * the navbar on every route, the tag chip strip is drawn above every listing
 * this app has, and the unread indicator sits in the top bar on every route. A
 * page cannot inject data into chrome it does not render, so chrome reads what
 * chrome draws.
 *
 * Both are `useQuery` — there is one cache in this repo and no route `loader`
 * performs a read.
 */
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@shared/api/client';
import { hasSession } from '@shared/auth/session';
import { asRows } from '@shared/api/envelope';
import { queryKeys, staleTimes } from '@shared/api/query-keys';

import type { GenreNode } from './GenreTree';

/**
 * `GET /api/v1/categories` — the genre tree, full depth, ordered by `position`
 * within each parent, for the navbar disclosure panel.
 *
 * A served VOCABULARY, so it sits on the vocabulary `staleTime`: an operator
 * adding a genre this morning does not need it re-fetched every thirty
 * seconds, and a tree that changes under a reader mid-browse is worse than one
 * that settles for five minutes.
 */
/**
 * One `/api/v1/categories` row, as the service serves it.
 *
 * **The tree arrives flat.** `data` is a bare array of table rows carrying
 * `parent_id`, `depth` and `position` — «full depth, ordered by `position`
 * within each parent» describes the ROWS. The disclosure panel walks
 * `children`, so the nesting is composed here, once.
 */
interface WireCategoryRow {
  id: string;
  parent_id: string | null;
  slug: string;
  name: string;
}

/**
 * Nest the flat rows into the tree the disclosure panel walks.
 *
 * **Nothing is re-sorted.** The service orders the rows `depth, position, name`,
 * so siblings already arrive in the rank an operator set, and a sort here would
 * make the ordering a client constant instead of the operator's data.
 *
 * A row whose `parent_id` names nothing in the payload is promoted to the root
 * rather than dropped: a genre missing from the navbar is worse than one drawn
 * a level too high, because the reader can see the second and not the first.
 */
function composeGenreTree(rows: readonly WireCategoryRow[]): GenreNode[] {
  const children = new Map<string, GenreNode[]>();
  const nodes = new Map<string, GenreNode>();

  for (const row of rows) {
    const bucket: GenreNode[] = [];
    children.set(row.id, bucket);
    nodes.set(row.id, { slug: row.slug, name: row.name, children: bucket });
  }

  const roots: GenreNode[] = [];
  for (const row of rows) {
    const node = nodes.get(row.id);
    if (!node) continue;
    const siblings = row.parent_id === null ? undefined : children.get(row.parent_id);
    if (siblings) siblings.push(node);
    else roots.push(node);
  }

  return roots;
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.content.categories(),
    queryFn: async () => ({
      items: composeGenreTree(asRows<WireCategoryRow>(await apiClient.get('/categories'))),
    }),
    staleTime: staleTimes.vocabulary,
    // The tree is an affordance, not the page. A failed read hides it rather
    // than taking the shell down with it.
    throwOnError: false,
  });
}

/** One chip of the strip. The service sends `id` and `position` too; neither is drawn. */
export interface TagChip {
  slug: string;
  name: string;
}

export interface TagStrip {
  /** Whether an operator curated the strip, or the service answered the whole vocabulary. */
  isCurated: boolean;
  items: TagChip[];
}

/**
 * `GET /api/v1/tagStrip` — the chips every listing on this app draws above it.
 *
 * **One read for every strip, and that is the point of it living here.** The
 * home page's strip and the two on `/genres/:slug` and `/tags/:slug` are ONE
 * behaviour in three compositions: an operator curates one ordered subset on the
 * console, and a reader meets the same chips wherever the strip appears. Two
 * reads against two sources is how the home page came to draw five curated chips
 * while the listing beside it drew all sixty-three.
 *
 * **Not `GET /tags`, which is the whole vocabulary**, and not the search
 * mirror's `tag_vocabulary` either — that one stays where it is, because
 * `/tags/:slug` still has to name a tag the operator curated AWAY, and a page
 * cannot title itself from a list its own subject may not be on.
 *
 * The «curated, else the whole vocabulary» fallback is the SERVICE's. Nothing
 * here branches on `isCurated`; a client that read the vocabulary and filtered
 * would be holding a second copy of a rule that would then drift.
 */
export function useTagStrip() {
  return useQuery({
    queryKey: queryKeys.content.tagStrip(),
    queryFn: async (): Promise<TagStrip> => {
      const payload = await apiClient.get<Partial<TagStrip>>('/tagStrip');
      return {
        isCurated: payload?.isCurated ?? false,
        items: asRows<TagChip>(payload?.items),
      };
    },
    staleTime: staleTimes.vocabulary,
    // The strip is an affordance, not the page: a failed read hides it.
    throwOnError: false,
  });
}

export interface UnreadCount {
  /**
   * ASCII on the wire, always, and **snake case** — `backend-notification`
   * spells every output field that way, and the dot stayed dark for as long as
   * this read asked for `unreadCount`.
   *
   * The dot is rendered from the boolean, not from the figure.
   */
  unread_count: number;
}

/**
 * `GET /api/v1/notifications/unreadCount` — the top bar's indicator.
 *
 * The row asks for an INDICATOR, not a count, so this figure never renders as
 * a number in the shell: it decides whether the dot is lit and nothing else.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: queryKeys.notification.unreadCount(),
    queryFn: () => apiClient.get<UnreadCount>('/notifications/unreadCount'),
    staleTime: staleTimes.list,
    throwOnError: false,
    /**
     * Never ask on behalf of nobody.
     *
     * `/404` and `/500` are deliberately unguarded — a reader on a dead address is more
     * lost than anywhere else — but they render inside the FULL shell, whose top bar
     * mounts this handle. `BootGate` waits for the boot refresh, which stops the read
     * racing a session that is about to exist; it cannot help when there is no session
     * at all, and an anonymous reader hitting a bad URL still fired this and collected a
     * red `401` in the console. There is nothing to indicate for a reader with no inbox,
     * so the honest fix is not to ask.
     */
    enabled: hasSession(),
  });
}
